import type { CustomerSubscription, SubscriptionStatus } from '../schemas/wash-package.js';

/**
 * Pure subscription-redemption math. Shared by:
 *   - the `redeemWashFromSubscription` Cloud Function trigger
 *   - the POS "active subscription" preview that decides whether to
 *     show the "Redeem 1 wash" affordance
 *   - the customer-pwa /me/subscriptions view that shows expiry +
 *     remaining-washes state
 *
 * No I/O, no Firebase types. Given a subscription doc + a checkout
 * context (service id, vehicle type, current time), returns a verdict
 * describing whether the subscription can be redeemed AND why-not when
 * it can't — every refusal carries a structured reason so the UI can
 * render a specific message instead of a generic "no".
 */

export type RedemptionRefusalReason =
  | 'expired'
  | 'cancelled'
  | 'paused'
  | 'exhausted'
  | 'service_not_eligible'
  | 'vehicle_not_eligible'
  | 'wrong_outlet';

export type RedemptionVerdict =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: RedemptionRefusalReason };

export interface RedemptionContext {
  /** Current subscription state from Firestore. */
  readonly subscription: CustomerSubscription;
  /** Where the redemption is being attempted. */
  readonly outletId: string;
  /** Service id the customer is buying. */
  readonly serviceId: string;
  /** Vehicle type of the customer's vehicle. Optional — empty array on subscription means "any". */
  readonly vehicleTypeId?: string;
  /** Now in ms-since-epoch. Passed in (not read from `Date.now()`) so tests can pin time. */
  readonly nowMs: number;
}

/**
 * Decide whether the given subscription can be redeemed for the given
 * service in this context. Verdict is "ok" only when EVERY guard passes;
 * the first failed guard sets the reason (status > exhaustion >
 * eligibility), which is the order the customer-pwa surfaces them.
 */
export function checkRedemption(ctx: RedemptionContext): RedemptionVerdict {
  const { subscription: s, outletId, serviceId, vehicleTypeId, nowMs } = ctx;

  // 1. Status guards
  if (s.status === 'cancelled') return { ok: false, reason: 'cancelled' };
  if (s.status === 'paused') return { ok: false, reason: 'paused' };
  if (s.status === 'expired') return { ok: false, reason: 'expired' };
  if (s.status === 'exhausted') return { ok: false, reason: 'exhausted' };

  // 2. Time guard — defence-in-depth against a stale status field.
  const expMs = tsMs(s.expiresAt);
  if (expMs > 0 && nowMs > expMs) {
    return { ok: false, reason: 'expired' };
  }

  // 3. Outlet scope — MVP redemption stays within the issuing outlet.
  //    Cross-outlet sharing within the same tenant lands later (would
  //    relax this guard to check `s.tenantId` only).
  if (s.outletId !== outletId) {
    return { ok: false, reason: 'wrong_outlet' };
  }

  // 4. Count-based exhaustion.
  if (s.packageSnapshot.kind === 'count' && s.washesRemaining <= 0) {
    return { ok: false, reason: 'exhausted' };
  }

  // 5. Service eligibility — empty array means "any service".
  const eligibleServices = s.packageSnapshot.eligibleServiceIds;
  if (eligibleServices.length > 0 && !eligibleServices.includes(serviceId)) {
    return { ok: false, reason: 'service_not_eligible' };
  }

  // 6. Vehicle-type eligibility — empty array means "any vehicle".
  const eligibleVehicles = s.packageSnapshot.eligibleVehicleTypes;
  if (eligibleVehicles.length > 0) {
    if (!vehicleTypeId || !eligibleVehicles.includes(vehicleTypeId)) {
      return { ok: false, reason: 'vehicle_not_eligible' };
    }
  }

  return { ok: true };
}

/**
 * Apply a single redemption to a subscription state. Pure: returns the
 * NEW state without mutating the input. The Cloud Function trigger
 * applies this inside a Firestore transaction so concurrent ticket
 * confirms can't double-deduct.
 *
 * Throws if `checkRedemption` would refuse — callers must verify first.
 */
export interface RedemptionResult {
  readonly nextWashesRemaining: number;
  readonly nextStatus: SubscriptionStatus;
  readonly exhaustedByThisRedemption: boolean;
}

export function applyRedemption(s: CustomerSubscription): RedemptionResult {
  if (s.packageSnapshot.kind === 'unlimited') {
    // Unlimited subs never decrement.
    return {
      nextWashesRemaining: s.washesRemaining,
      nextStatus: s.status,
      exhaustedByThisRedemption: false,
    };
  }
  if (s.washesRemaining <= 0) {
    throw new Error('Cannot redeem from an already-exhausted subscription.');
  }
  const next = s.washesRemaining - 1;
  const exhausted = next === 0;
  return {
    nextWashesRemaining: next,
    nextStatus: exhausted ? 'exhausted' : s.status,
    exhaustedByThisRedemption: exhausted,
  };
}

/**
 * Compute the "expiring soon" hint for the customer-pwa. Returns the
 * number of days until expiry, capped at a large positive integer for
 * far-future dates so the UI can pick a copy variant.
 */
export function daysUntilExpiry(s: CustomerSubscription, nowMs: number): number {
  const expMs = tsMs(s.expiresAt);
  if (expMs <= 0) return Number.POSITIVE_INFINITY;
  const diffMs = expMs - nowMs;
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}
